<template>
  <div
    class="dropdown-select"
    :class="{
      disabled: isDisabled,
      open: open,
    }"
    @click="clickListener"
  >
    <div ref="label" class="dropdown-select__label" v-html="labelParser(selection)" />
    <div ref="options" class="dropdown-select__options">
      <div v-if="multiple" class="dropdown-select__batch-actions">
        <span class="dropdown-select__actions-label">Select:</span>
        <a
          v-if="size ? optIds.length <= size : true"
          class="dropdown-select__action all-action"
          :disabled="options.length == selection.length"
          data-action="all"
          @click="selectAll"
        >All</a>
        <a
          v-if="availableGroups.length"
          class="dropdown-select__action"
          :class="{ active: groupSelection }"
          data-action="group"
          @click="groupSelection = !groupSelection"
        >By group</a>
        <a
          class="dropdown-select__action"
          :disabled="selection.length == 0"
          data-action="clear"
          @click="selection = []"
        >Clear</a>
      </div>
      <div v-if="groupSelection" class="dropdown-select__groups">
        <div
          v-for="(group, i) in availableGroups"
          :key="i"
          class="dropdown-select__group"
          @click="selectGroup(group)"
        >
          {{ group }}
        </div>
      </div>
      <div ref="slot-container"><slot /></div>
      <ul>
        <dropdown-option
          v-for="(option, i) in options"
          :key="i"
          v-bind="option"
          :depth="0"
          :selection="selection"
          @select="onSelect"
        />
      </ul>
    </div>
  </div>
</template>

<script>
import DropdownOption from './DropdownOption.vue'
import { flatten as flattenTree } from '@/utils/tree.js'

export default {
  components: {
    'dropdown-option': DropdownOption,
  },
  model: {
    prop: 'value',
    event: 'change',
  },
  props: ['value', 'options', 'multiple', 'disabled', 'size', 'labelParser', 'closeOnSelect', 'groups'],
  data() {
    return {
      open: false,
      groupSelection: false
    }
  },
  computed: {
    isDisabled() {
      return this.disabled || this.options.length === 0
    },
    label() {
      return this.multiple ? this.labelParser() : this.selection[0].label
    },
    selection: {
      get() {
        return this.multiple ? this.value.map(d => d.id) : [this.value.id]
      },
      set(ids) {
        const selection = flattenTree(this.options).filter(opt => ids.includes(opt.id))
        this.$emit('change', selection)
        if (!this.multiple || this.closeOnSelect) this.open = false
      },
    },
    optIds() {
      return flattenTree(this.options).map(d => d.id)
    },
    availableGroups() {
      if (!(this.groups || Array.isArray(this.groups))) return [];

      const ids = flattenTree(this.options).map(c => c.id);
      return Object.keys(this.groups).reduce((groups, group) => {
        const isAvailable = this.groups[group].filter(id => ids.includes(id)).length > 0;
        if (isAvailable) groups.push(group);
        return groups;
      }, []);
    }
  },
  watch: {
    open(to, from) {
      if (to && !from) {
        document.addEventListener('click', this.onClickOut, true)
        this.$refs.options.style.top = this.$refs.label.offsetHeight
        this.$emit('open')
      } else {
        document.removeEventListener('click', this.onClickOut, true)
        // this.$refs.options.style.top = 0
        this.$emit('close')
      }
    },
    options: {
      handler(to) {
        if (to && to.length) {
          to.forEach(opt => {
            opt.label = opt.labels[this.lng]
            if (opt.children) {
              opt.children.forEach(chld => {
                chld.label = chld.labels[this.lng]
              })
            }
          })
        }
      },
      immediate: true,
    },
  },
  mounted() {
    const offset = this.$el.getBoundingClientRect()
    this.$refs.options.style.maxHeight =
      window.innerHeight -
      (offset.top + offset.height) -
      window.innerHeight * 0.1 +
      'px'
  },
  methods: {
    broadcast(event, payload) {
      this.$emit(event, payload)
    },
    onSelect(optionId) {
      this.size ? this.selection.length >= this.size : false

      if (this.multiple) {
        const index = this.selection.findIndex(d => d === optionId)
        if (index >= 0) {
          this.selection = this.selection
            .slice(0, index)
            .concat(this.selection.slice(index + 1, this.selection.length))
        } else if (this.size && this.selection.length < this.size || !this.size) {
          this.selection = this.selection.concat(optionId)
        }
      } else {
        this.selection = [optionId]
      }
    },
    selectAll() {
      this.selection = flattenTree(this.options)
        .filter(opt => opt.isValid)
        .map(({ id }) => id)
    },
    onClickOut(ev) {
      if (!this.$el.contains(ev.target)) {
        ev.stopPropagation()
        ev.stopImmediatePropagation()
        ev.preventDefault()
        this.open = false
      }
    },
    clickListener(ev) {
      if (this.isDisabled) return
      if (
        this.multiple &&
        this.open &&
        this.$refs.options.contains(ev.target) &&
        (this.closeOnSelect !== true || this.$refs['slot-container'].contains(ev.target))
      )
        return
      this.open = !this.open
    },
    selectGroup(groupName) {
      this.$emit('change', flattenTree(this.options).filter(opt => {
        return this.groups[groupName].includes(opt.id);
      }));
    }
  },
}
</script>

<style lang="stylus">
.dropdown-select
  align-items: center
  border: 1px solid transparent
  position: relative
  margin-right: vw(20px)

  .mobile &
    margin-right: 0px

  &.disabled
    .dropdown-select__label
      cursor: default
      opacity: .6

    :after
      display: none

  &.open
    border-color: $gray
    border-top-left-radius: 20px
    border-top-right-radius: 20px

    .mobile &
      border-color: transparent

    .dropdown-select__label:after
      transform: translateY(-50%) rotate(180deg)

    .dropdown-select__options
      display: block

.dropdown-select__label
  color: $black
  cursor: pointer
  padding: vw(7.5px) vw(10px)
  padding-right: vw(20px)
  text-align: start
  text-indent: vw(5px)
  m-ellipsis(100%)

  > label
    opacity: 0.7

  .mobile &
    padding: vw(2px) 0 
    m-font-size(13, 14)

    +m-breakpoint(landscape)
      font-size: 0.7em
      line-height: 1.1
      padding: vh(5px) vw(10px)
      padding-left: 0px

  &:after
    content: ""
    position: absolute
    m-icon("arrow-down", 25)
    padding: 0
    right: vw(5px)
    top: 50%
    transform: translateY(-50%) rotate(0)
    transition: transform 0.2s ease-out

    .mobile &
      m-icon("arrow-down", 15)
      right: 0

      +m-breakpoint(landscape)
        right: vw(5px)
        m-icon("arrow-down", 12)

.dropdown-select__options
  border: 1px solid $gray
  border-bottom-left-radius: 10px
  border-bottom-right-radius: 10px
  border-radius: unset
  display: none
  left: -1px
  overflow-x: hidden
  overflow-y: auto
  position: absolute
  // top: 38px
  width: calc(100% + 2px)
  z-index: 100

  .mobile &
    top: 30px
    border-radius: 10px
    max-height: 50vh !important

  .dropdown-select__batch-actions
    display: flex
    background-color: white
    padding: vw(10px) vw(10px) vw(2px) vw(20px)
    m-font-size(10, 12)
    line-height: 1.2

    .mobile &
      padding: vw(10px) vw(10px) vh(15px) vw(10px)

      +m-breakpoint(landscape)
        padding: vh(10px) vw(10px)
        m-font-size(8, 10)

  .dropdown-select__actions-label,
  .dropdown-select__action

    +m-breakpoint(landscape)
      .mobile &
        font-size: 0.6em

  .dropdown-select__actions-label
    opacity: 0.6

  .dropdown-select__action
    color: $blue-2
    cursor: pointer
    padding-left: 0.5em

    + .dropdown-select__action:not(:last-child)
      border-left: 0.5px solid lightgray
      margin-left: 0.5em

    &[data-action="group"]
      position: relative

      &.active:after
        transform: rotate(-135deg)

        .mobile &
          +m-breakpoint(landscape)
            transform: translateY(0.6em) rotate(-135deg)

      &:after
        position: absolute
        content: ""
        right: -1.4em
        top: 0
        transform: translateY(-0.15em) rotate(45deg)
        width: 0.5em
        height: 0.5em
        margin: 0.4em
        border-bottom: 1px solid
        border-right: 1px solid
        transition: transform 200ms ease

        .mobile &
          +m-breakpoint(landscape)
            transform: translateY(0.3em) rotate(45deg)

    &:last-child
      margin-left: auto

    &[disabled]
      color: $black
      cursor: default

  .dropdown-select__groups
    display: flex
    flex-wrap: wrap
    padding: vw(10px) vw(10px) vw(5px) vw(20px)
    background-color: white

    .mobile &
      padding: vh(20px) vw(10px) vh(15px) vw(20px)

      +m-breakpoint(landscape)
        padding: vh(5px) vw(10px)

    > .dropdown-select__group
      m-font-size(10, 15)
      background-color: rgba($green, 0.2)
      border-radius: 5px
      color: $green
      cursor: pointer
      display: inline-flex
      margin-bottom: vw(5px)
      margin-right: vw(5px)
      padding: vw(2px) vw(5px)

      .mobile &
        font-size: 0.75em
        line-height: 1.3

        +m-breakpoint(landscape)
          margin-bottom: vw(3px)
          margin-right: vw(3px)
          padding: vh(2px) vw(3px)

      &:hover
        background-color: rgba($green, 0.5)

  ul
    background: #fff
    m-font-size(12, 16)

    .mobile &
      +m-breakpoint(landscape)
        font-size: 0.65rem

</style>
